// Text normalisation shared by the search scorer and the synonym vocabulary.
//
// These live apart from search.js so synonyms.js can fold its word lists with exactly the
// same rules the scorer applies to a query, without the two importing each other.

// Lowercase, strip accents, and turn anything that isn't a letter or digit into a space.
// Punctuation splitting is what makes "pencil (red)" and "3.5mm jack" searchable by any of
// their parts, and it has to happen identically to queries and to indexed text.
export function normalizeText(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function tokenize(query) {
  const norm = normalizeText(query);
  return norm ? norm.split(" ") : [];
}

// Crude English plural folding, applied to both sides so "batteries" finds "battery" and
// "screw" finds "screws". Wrong for irregulars, which costs nothing: an unmatched fold just
// falls through to the other tiers. Irregular pairs that matter are listed explicitly in
// synonyms.js instead ("knife"/"knives").
export function foldPlural(word) {
  if (word.length > 4 && word.endsWith("ies")) return word.slice(0, -3) + "y";
  if (word.length > 4 && (word.endsWith("ches") || word.endsWith("shes") || word.endsWith("xes") || word.endsWith("sses"))) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith("s") && !word.endsWith("ss") && !word.endsWith("us")) return word.slice(0, -1);
  return word;
}

// True when `a` and `b` are within `max` single-character edits. Bails on the length
// difference first, so the common case costs almost nothing.
export function withinEditDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return false;
  if (a === b) return true;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i];
    let rowBest = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + cost);
      if (row[j] < rowBest) rowBest = row[j];
    }
    if (rowBest > max) return false; // no cell in this row can lead anywhere cheap enough
    prev = row;
  }
  return prev[b.length] <= max;
}
